import { cn } from '@/utils/shad';
import { Cross2Icon } from '@radix-ui/react-icons';
import { VariantProps, cva } from 'class-variance-authority';
import { PropsWithChildren } from 'react';
import { EventFor, WithBaseElementGlobalAttributes, WithOnClick } from '@/types/component';

export const chipVariants = cva('inline-block rounded-base', {
  variants: {
    variant: {
      positive: 'bg-[#8CC30A]/20 border-[#8CC30A]/20',
      negative: 'bg-[rgba(172,30,30,0.39)] border-[#ED512E]/20',
      danger: 'bg-[#ED512E]',
      neutral: 'bg-[#E2E54A]/20 border-[#E2E54A]/20',
      info: 'bg-[#1C94EB]',
      tertiary: 'bg-tertiary/20 border-tertiary/20',
      quaternary: 'bg-quaternary/20 border-quaternary/20',
      ghost: 'border-muted text-muted-foreground',
      muted: 'border-muted text-muted-foreground bg-[#18181A]',
      secondary: 'text-foreground border-muted border bg-[#27272A]/80',
      mutedv2: 'text-muted-foreground bg-[#18181A] items-center gap-1 rounded-lg',
      mutedv3: 'text-muted-foreground bg-[#27272A]/80 border border-[#27272A] items-center gap-1 rounded-md',
      accent: 'text-accent-foreground bg-background border',
      purple: 'border bg-[#924CEC26] text-white',
    },
    size: {
      small: 'px-1 py-0',
      medium: 'px-2',
    },
  },
  defaultVariants: {
    variant: 'info',
    size: 'medium',
  },
});

export const percentageToVariant = (percentage: number) => {
  if (percentage < 0.65) {
    return 'negative' as const;
  } else if (percentage < 0.85) {
    return 'neutral' as const;
  } else {
    return 'positive' as const;
  }
};

export type ChipProps = VariantProps<typeof chipVariants> &
  PropsWithChildren &
  WithBaseElementGlobalAttributes &
  WithOnClick<EventFor<'span', 'onClick'>>;

export const Chip = ({ onClick, style, className, children, variant, size }: ChipProps) => {
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={onClick}
      className={cn(
        'hover inline-block hover:cursor-auto',
        chipVariants({ size: size, variant: variant }),
        onClick && 'hover:cursor-pointer',
        className,
      )}
      style={style}
    >
      {children}
    </span>
  );
};

export const statusChipVariants = cva('inline-block rounded-md', {
  variants: {
    variant: {
      positive: 'bg-positive/20 text-positive',
      negative: 'bg-negative/20 text-negative',
      neutral: 'bg-neutral/20 text-neutral',
      secondary: 'bg-[#25384F] text-secondary-foreground',
    },
    size: {
      small: 'px-1 py-0',
      medium: 'px-2',
      lg: 'py-1 pl-1.25 pr-base rounded-full',
    },
  },
  defaultVariants: {
    variant: 'positive',
    size: 'medium',
  },
});

export interface StatusChipProps extends VariantProps<typeof statusChipVariants>, React.PropsWithChildren {
  className?: string;
}

export const StatusChip = (props: StatusChipProps) => {
  return (
    <span className={cn(statusChipVariants({ className: props.className, variant: props.variant, size: props.size }))}>
      {props.children}
    </span>
  );
};

export type DeletableChipProps = React.PropsWithChildren<{
  className?: string;
  onDelete: () => void;
  disabled?: boolean;
}>;

export const DeletableChip = ({ className, children, onDelete, disabled }: DeletableChipProps) => {
  return (
    <Chip size="small" className={cn('flex items-center gap-1 border bg-card px-2 py-1', className)}>
      {children}
      {!disabled && (
        <Cross2Icon
          className="h-4 w-4 transition-colors hover:cursor-pointer hover:text-red-500"
          onClick={() => onDelete()}
        />
      )}
    </Chip>
  );
};
